import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

/** §3.1 upload store. `File` objects live here (outside React render paths) and are excluded from the IDB mirror. */
export type UploadStatus = 'pending' | 'hashing' | 'initiating' | 'uploading' | 'completing' | 'processing' | 'ready' | 'failed' | 'paused' | 'cancelled';

export type UploadPartState = { etag?: string; loaded: number; attempts: number; url?: string; expires_at?: number };

export type UploadRecord = {
  client_upload_id: string;
  file: File | null;
  handle?: FileSystemFileHandle;
  name: string;
  size: number;
  lastModified: number;
  detected_mime: string;
  target_board_id: string;
  workspace_id: string;
  asset_id?: string;
  version_id?: string;
  upload_id?: string;
  part_size?: number;
  part_count?: number;
  sha256?: string;
  hash_kind?: 'full' | 'sampled';
  preview?: { blobUrl: string; width: number; height: number } | null;
  parts: Map<number, UploadPartState>;
  bytesSent: number;
  status: UploadStatus;
  error?: { code: string; message: string; retryable: boolean };
  processing_progress?: number;
  hash_progress?: number;
  createdAt: number;
  guest_jwt?: string;
  /** true when this upload creates a new version of an existing asset (§3.5) */
  is_new_version: boolean;
  duplicateOf?: string | null;
  /** hashing + preview finished → eligible for the scheduler */
  prepared: boolean;
  /** server state is "resume needs file" (after reload) */
  needsFile?: boolean;
};

export type PendingResume = { client_upload_id: string; name: string; size: number; lastModified: number; target_board_id: string };

export type UploadState = {
  records: Record<string, UploadRecord>;
  order: string[];
  active: Set<string>;
  paused: boolean;
  pausedReason: 'offline' | 'saveData' | 'user' | null;
  clientUploadByAsset: Record<string, string>;
  pendingResume: PendingResume[];
  panelCollapsed: boolean;
  setPanelCollapsed: (v: boolean) => void;
};

export const useUploadStore = create<UploadState>()((set) => ({
  records: {},
  order: [],
  active: new Set(),
  paused: false,
  pausedReason: null,
  clientUploadByAsset: {},
  pendingResume: [],
  panelCollapsed: false,
  setPanelCollapsed: (panelCollapsed) => set({ panelCollapsed }),
}));

/** Per-record selector (§3.1): a progress tick re-renders one row, not the panel. */
export function useUploadRecord(id: string): UploadRecord | undefined {
  return useUploadStore((s) => s.records[id]);
}
export function useUploadOrder(): string[] {
  return useUploadStore((s) => s.order);
}
export function useUploadSummary() {
  return useUploadStore(
    useShallow((s) => {
      let uploading = 0;
      let failed = 0;
      let done = 0;
      let bytes = 0;
      let sent = 0;
      for (const id of s.order) {
        const r = s.records[id];
        if (!r) continue;
        if (r.status === 'ready') done++;
        else if (r.status === 'failed') failed++;
        else if (r.status !== 'cancelled') uploading++;
        if (r.status !== 'cancelled' && r.status !== 'failed') {
          bytes += r.size;
          sent += r.status === 'ready' || r.status === 'processing' || r.status === 'completing' ? r.size : r.bytesSent;
        }
      }
      return { uploading, failed, done, total: s.order.length, fraction: bytes ? sent / bytes : 0, paused: s.paused, pausedReason: s.pausedReason, pendingResume: s.pendingResume.length };
    }),
  );
}

/** Internal mutators used by the manager (not React). */
export function patchRecord(id: string, patch: Partial<UploadRecord> | ((r: UploadRecord) => Partial<UploadRecord>)): UploadRecord | undefined {
  let out: UploadRecord | undefined;
  useUploadStore.setState((s) => {
    const r = s.records[id];
    if (!r) return s;
    const p = typeof patch === 'function' ? patch(r) : patch;
    out = { ...r, ...p };
    return { records: { ...s.records, [id]: out } };
  });
  return out;
}
export function getRecord(id: string): UploadRecord | undefined {
  return useUploadStore.getState().records[id];
}
